import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { CheckCircle, Mail } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';

import Button from '../../components/Button';
import Icon from '../../components/Icon/Icon';
import { track } from '../../lib/analytics';
import { contentQueries, useGetContent } from '../../lib/react-query/ContentQueries';
import { projectQueries, useGetProjectById } from '../../lib/react-query/ProjectQueries';

const searchParamsSchema = z.object({
  id: z.string(),
});

export const Route = createFileRoute('/tatouage/confirmation')({
  component: ConfirmationPage,
  validateSearch: searchParamsSchema,
  loaderDeps: ({ search }) => ({ id: search.id }),
  loader: async ({ context, deps }) => {
    const { queryClient } = context;
    const { id } = deps;

    // Preload content (always needed) and attempt to load project
    const [project] = await Promise.all([
      queryClient.ensureQueryData(projectQueries.byId(id)).catch(() => null),
      queryClient.ensureQueryData(contentQueries.main()),
    ]);

    if (!project) {
      throw redirect({ to: '/tatouage' });
    }
  },
  head: () => ({
    meta: [
      {
        title: 'Confirmation de projet tatouage | Azelysse Piercing & Tattoo',
      },
      {
        name: 'robots',
        content: 'noindex, nofollow',
      },
    ],
  }),
});

function ConfirmationPage() {
  const { id } = Route.useSearch();
  const navigate = useNavigate();
  const tracked = useRef(false);
  const { data: content } = useGetContent();
  const { data: project, isFetching, isError } = useGetProjectById(id);

  useEffect(() => {
    if (project && !tracked.current) {
      track('ProjetConfirmation');
      tracked.current = true;
    }
  }, [project]);

  const tattooInstagram = useMemo(
    () => content?.instagram?.filter((insta) => insta.type === 'tattoo')[0],
    [content?.instagram],
  );

  useEffect(() => {
    if (!id || (isError && !isFetching)) {
      navigate({ to: '/tatouage' });
    }
  }, [id, isError, isFetching, navigate]);

  if (isFetching) {
    return (
      <div className="mx-auto max-w-7xl px-6 pt-36 pb-32 sm:pt-48 lg:px-8 lg:pt-32">
        <p>Chargement...</p>
      </div>
    );
  }

  if (!project || !content) {
    return null;
  }

  const images = project.images;

  return (
    <div className="mx-auto max-w-7xl px-6 pt-36 pb-32 sm:pt-48 lg:px-8 lg:pt-32">
      <div className="mx-auto max-w-2xl min-w-0">
        <div className="mb-6 flex items-center gap-3">
          <CheckCircle className="h-10 w-10 shrink-0 text-emerald-600" />
          <h1 className="text-2xl font-semibold">Projet de tatouage envoyé !</h1>
        </div>

        <div className="mb-8 flex flex-col gap-2 text-neutral-700">
          <p>Vous allez recevoir un e-mail récapitulatif de votre projet.</p>
          <p>
            Je vais étudier votre demande et vous recontacter rapidement pour discuter de votre
            projet et planifier une consultation.
          </p>
        </div>

        <div className="mb-8 rounded-lg border border-emerald-200 bg-emerald-50/60 p-6 wrap-anywhere">
          <h2 className="mb-4 text-lg font-semibold text-emerald-900">Récapitulatif</h2>

          {project.description && (
            <>
              <p className="mb-1 text-sm font-medium text-neutral-500">Détails</p>
              <div
                className="mb-4 text-sm wrap-break-word text-neutral-800 [&_a]:break-all [&_a]:text-emerald-700 [&_a]:underline [&_li]:ml-5 [&_li]:list-disc [&_p]:leading-relaxed [&_p+p]:mt-2 [&_ul]:mt-1 [&_ul]:mb-2"
                dangerouslySetInnerHTML={{ __html: project.description }}
              />
            </>
          )}

          {images.length > 0 && (
            <>
              <p className="mb-2 text-sm font-medium text-neutral-500">Photos d'inspiration</p>
              <div className="flex flex-wrap gap-3">
                {images.map((image) => (
                  <img
                    key={image.id}
                    src={image.image_url}
                    alt="Photo d'inspiration"
                    className="max-h-48 rounded-lg object-contain"
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="mb-8 rounded-lg border border-neutral-200 bg-white p-6">
          <h2 className="mb-3 text-lg font-semibold">Des questions ?</h2>
          <p className="mb-4 text-sm text-neutral-600">
            N'hésitez pas à me contacter pour toute question concernant votre projet.
          </p>
          <div className="flex flex-wrap gap-3">
            {content.contact?.email && (
              <a
                href={`mailto:${content.contact.email}`}
                className="inline-flex items-center gap-2 rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-700 transition hover:bg-neutral-200"
              >
                <Mail className="h-4 w-4" />
                E-mail
              </a>
            )}
            {tattooInstagram?.url && (
              <a
                href={tattooInstagram.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-700 transition hover:bg-neutral-200"
              >
                <Icon name="InstagramIcon" />
                Instagram
              </a>
            )}
          </div>
        </div>

        <div>
          <Link to="/tatouage">
            <Button className="bg-emerald-700 text-white hover:bg-emerald-600" color="primary">
              Retour à la page tatouage
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
